import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isPrerender } from '../lib/prerender';
import { Link } from 'react-router-dom';
import { Star, ExternalLink, PenSquare, ChevronLeft, ChevronRight } from 'lucide-react';
import Reveal from './Reveal';
import { trackEvent } from '../analytics';
import {
  REVIEW_PLATFORMS,
  PRIMARY_REVIEW_PLATFORM,
} from '../reviewPlatforms';
import {
  REVIEW_COUNT,
  REVIEW_RATING,
} from '../constants';
import { REVIEWS } from '../data/reviews';

// ─────────────────────────────────────────────────────────────────────────────
// Google Reviews — primary trust element, sitewide.
//
// TWO RENDER MODES
//   • Default (full) — headline + a horizontal carousel of the verbatim reviews +
//     two action buttons. Rendered on Home at #reviews.
//   • compact        — stars + count + a single "See my reviews" pill link
//                      to /#reviews. Used on every service page.
//
// AUTHENTICITY RULE
//   Data lives in /src/data/reviews.js and is used verbatim (see the header
//   in that file). Cards clamp overflow visually with a "Read more" control
//   but the full text is always in the DOM — nothing is truncated at the
//   data layer.
// ─────────────────────────────────────────────────────────────────────────────

// Verified star rating, retaining the existing visual treatment.
const StarRow = ({ count = 5, size = 16, testId }) => (
  <span className="gr-stars" role="img" aria-label={`${count} out of 5 stars`} data-testid={testId}>
    {Array.from({ length: count }).map((_, i) => (
      <Star key={i} size={size} strokeWidth={0} fill="currentColor" aria-hidden="true" />
    ))}
  </span>
);

// Outbound button — reads its URL from the platform config, which itself reads
// from /src/constants.js for the Google URLs. Never a dead link.
const PlatformButton = ({ platform, kind, location }) => {
  const isView = kind === 'view';
  const url = isView ? platform.viewProfileUrl : platform.writeReviewUrl;
  const eventName = isView ? platform.viewEventName : platform.writeEventName;
  const label = isView ? `See my ${platform.name} reviews` : `Leave me a ${platform.name} review`;
  const Icon = isView ? ExternalLink : PenSquare;
  const testid = `gr-${platform.key}-${isView ? 'view' : 'write'}-btn`;

  if (!url) {
    return (
      <span className="gr-cta-pending" data-testid={`${testid}-pending`} aria-live="polite">
        {isView
          ? `${platform.name} profile link coming shortly.`
          : `${platform.name} write-a-review link coming shortly.`}
      </span>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`btn ${isView ? 'btn-primary' : 'btn-outline'} gr-cta-btn`}
      data-testid={testid}
      onClick={() => trackEvent(eventName, { location, platform: platform.key })}
    >
      <Icon size={18} strokeWidth={2.2} aria-hidden="true" />
      <span>{label}</span>
    </a>
  );
};

// ─────────── One review card ───────────
// Own state per card: whether it's expanded, and whether its text overflows
// the 7-line clamp (so the Read more button only shows when there's actually
// more to reveal). Set `clampable={false}` to render the full text with no
// clamp and no Read more control — used by the service-page static rows.
export const ReviewCard = ({ review, index, clampable = true }) => {
  const { name, tag, text } = review;
  const bodyRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);

  useLayoutEffect(() => {
    if (!clampable || !text) return;
    // Build-time pre-render: keep canExpand=false so static HTML matches the
    // client's first hydration render (see lib/prerender.js).
    if (isPrerender()) return;
    const el = bodyRef.current;
    if (!el) return;
    const check = () => {
      if (!expanded) setCanExpand(el.scrollHeight > el.clientHeight + 1);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, expanded, clampable]);

  const clampOn = clampable && !expanded;
  const testid = `gr-card-${index}`;
  return (
    <figure
      className={`gr-card${expanded ? ' gr-card--expanded' : ''}`}
      data-testid={testid}
    >
      <StarRow count={review.rating ?? 5} testId={`gr-stars-${index}`} />
      {tag && (
        <p className="gr-card-headline" data-testid={`gr-headline-${index}`}>{tag}</p>
      )}
      {text && (
        <blockquote
          ref={bodyRef}
          className={`gr-card-body${clampOn ? ' gr-card-body--clamp' : ''}`}
          data-testid={`gr-body-${index}`}
        >
          {text}
        </blockquote>
      )}
      {clampable && text && canExpand && (
        <button
          type="button"
          className="gr-read-more"
          data-testid={`gr-read-more-${index}`}
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}
      <figcaption className="gr-card-attr">
        <span className="gr-card-name" data-testid={`gr-name-${index}`}>{name}</span>
        <span className="gr-card-source" data-testid={`gr-source-${index}`}>on Google</span>
      </figcaption>
    </figure>
  );
};

// ─────────── Static row of 3 named review cards (for service pages) ───────────
// Resolves review names from data/reviews.js. Never retypes text — a name
// that doesn't match an entry logs a warning and is skipped rather than
// silently rendering wrong data.
export const StaticReviewsRow = ({ names }) => {
  const picks = (names || [])
    .map((n) => {
      const found = REVIEWS.find((r) => r.name === n);
      if (!found) console.warn(`[GoogleReviews] no review named ${JSON.stringify(n)}`);
      return found;
    })
    .filter(Boolean);
  return (
    <div className="gr-static-row" role="list" data-testid="gr-static-row">
      {picks.map((r, i) => (
        <ReviewCard key={`${r.name}-${i}`} review={r} index={i} clampable={false} />
      ))}
    </div>
  );
};

// ─────────── Compact variant (service pages) ───────────
const CompactBadge = () => {
  const enabled = REVIEW_PLATFORMS.filter((p) => p.enabled);
  const gp = PRIMARY_REVIEW_PLATFORM;
  return (
    <section
      className="gr-compact"
      id="reviews-compact"
      data-testid="google-reviews-compact"
      aria-label="Google reviews summary"
    >
      <div className="container">
        <div className="gr-compact-inner">
          <div className="gr-compact-rating" data-testid="gr-compact-rating">
            <span className="gr-compact-num" data-testid="gr-compact-number">{REVIEW_RATING}</span>
            <StarRow size={18} testId="gr-compact-stars" />
            <span className="gr-compact-count" data-testid="gr-compact-count">{`from ${REVIEW_COUNT} Google reviews`}</span>
          </div>
          <div className="gr-compact-actions">
            <Link
              to="/#reviews"
              className="gr-compact-link"
              data-testid="gr-compact-see-all"
              onClick={() => trackEvent('reviews_anchor_click', { location: 'service_compact' })}
            >
              Read reviews
            </Link>
            {enabled.length > 0 && (
              <PlatformButton platform={gp} kind="view" location="service_compact" />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// ─────────── Full block — carousel ───────────
const FullReviewsBlock = ({ editorial = false, nearHero = false }) => {
  const total = REVIEWS.length;
  const enabledPlatforms = REVIEW_PLATFORMS.filter((p) => p.enabled && (!editorial || p.key === 'google'));
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const swipeFiredRef = useRef(false);

  // Fire the GA4-only engagement event on the first swipe/click per session.
  // Never mapped to a Meta standard event (see analytics.js — deliberate).
  const fireSwipeOnce = useCallback(() => {
    if (swipeFiredRef.current) return;
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        if (window.sessionStorage.getItem('fairfix_reviews_swipe_fired') === '1') {
          swipeFiredRef.current = true;
          return;
        }
        window.sessionStorage.setItem('fairfix_reviews_swipe_fired', '1');
      }
    } catch (_) { /* private mode / disabled storage — still fire once per tab */ }
    swipeFiredRef.current = true;
    trackEvent('reviews_swipe');
  }, []);

  // Compute the current "active" card = leftmost card whose left edge is at
  // or past the track's scrollLeft. Used for the "X of N" counter.
  const recomputeActive = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const cards = track.querySelectorAll('[data-review-card]');
    if (!cards.length) return;
    const trackLeft = track.getBoundingClientRect().left;
    let bestIdx = 0;
    let bestDist = Infinity;
    cards.forEach((el, i) => {
      const d = Math.abs(el.getBoundingClientRect().left - trackLeft);
      if (d < bestDist) { bestDist = d; bestIdx = i; }
    });
    setActive(bestIdx);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let raf = 0;
    const onScroll = () => {
      fireSwipeOnce();
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        recomputeActive();
      });
    };
    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', recomputeActive);
    recomputeActive();
    return () => {
      track.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', recomputeActive);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [recomputeActive, fireSwipeOnce]);

  const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const stepBy = (dir) => {
    const track = trackRef.current;
    if (!track) return;
    fireSwipeOnce();
    // Step by one card's outer width (first card + its right margin/gap).
    const first = track.querySelector('[data-review-card]');
    if (!first) return;
    const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '0') || 0;
    const step = first.getBoundingClientRect().width + gap;
    track.scrollBy({
      left: dir * step,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  const onTrackKeyDown = (e) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      stepBy(1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      stepBy(-1);
    }
  };

  return (
    <section
      className={`section google-reviews${editorial ? ' google-reviews--editorial' : ''}${nearHero ? ' google-reviews--near-hero' : ''}`}
      id="reviews"
      data-testid="google-reviews-section"
      aria-label="Google reviews for FairFix Construction"
    >
      <div className="container">
        <Reveal as="div" className="section-head gr-head">
          {nearHero ? <>
            <h2 className="section-h2 gr-title">Google reviews</h2>
            <p className="gr-summary" data-testid="gr-summary"><span data-testid="gr-summary-rating">{REVIEW_RATING}</span><StarRow size={12} testId="gr-summary-stars" /><span data-testid="gr-summary-count">{REVIEW_COUNT} reviews</span></p>
          </> : <>
          <p className="gr-eyebrow">Google reviews</p>
          <h2 className="section-h2 gr-title">
            {editorial ? <>Kind words. <em>Real experiences.</em></> : <>
              <span className="gr-rating" data-testid="gr-rating">{REVIEW_RATING}</span>
              <StarRow size={22} testId="gr-rating-stars" />
              <span className="gr-count" data-testid="gr-count">{`from ${REVIEW_COUNT} Google reviews`}</span>
            </>}
          </h2>
          <p className="section-lead gr-lead">
            A selection of customer reviews. Read more on Google.
          </p>
          </>}
        </Reveal>

        <div className="gr-carousel" data-testid="gr-carousel">
          <div
            ref={trackRef}
            className="gr-track"
            role="list"
            tabIndex={0}
            aria-label={`Google reviews carousel, ${total} reviews`}
            data-testid="gr-track"
            onKeyDown={onTrackKeyDown}
          >
            {REVIEWS.map((r, i) => (
              <div className="gr-slide" data-review-card key={`${r.name}-${i}`}>
                <ReviewCard review={r} index={i} />
              </div>
            ))}
          </div>

          <div className="gr-nav" data-testid="gr-nav">
            <button
              type="button"
              className="gr-nav-btn"
              aria-label="Previous review"
              data-testid="gr-prev-btn"
              onClick={() => stepBy(-1)}
            >
              <ChevronLeft size={22} strokeWidth={2.4} aria-hidden="true" />
            </button>
            <span
              className="gr-counter"
              role="status"
              aria-live="polite"
              data-testid="gr-counter"
            >
              {`${active + 1} of ${total}`}
            </span>
            <button
              type="button"
              className="gr-nav-btn"
              aria-label="Next review"
              data-testid="gr-next-btn"
              onClick={() => stepBy(1)}
            >
              <ChevronRight size={22} strokeWidth={2.4} aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="gr-actions" data-testid="gr-actions">
          {enabledPlatforms.map((platform) => (
            <div key={platform.key} className="gr-actions-group" data-testid={`gr-actions-${platform.key}`}>
              <PlatformButton platform={platform} kind="view"  location="reviews_section" />
              {!editorial && <PlatformButton platform={platform} kind="write" location="reviews_section" />}
            </div>
          ))}
        </div>
      </div>

      {/* SEO decision 2026-09-11: NO AggregateRating / Review JSON-LD here.
          Google's review-snippet guidelines make a business's own
          LocalBusiness/Organization reviews (incl. Google-review widgets)
          ineligible for review stars — self-serving markup is a policy risk,
          not a ranking asset. Visible cards and the manually verified shared
          aggregate remain separate from structured data. Do not re-add. */}
    </section>
  );
};

// Thin router — never runs hooks itself, so compact and full each get their
// own stable hook order (fixes react-hooks/rules-of-hooks).
const GoogleReviews = ({ compact = false, editorial = false, nearHero = false }) =>
  compact ? <CompactBadge /> : <FullReviewsBlock editorial={editorial} nearHero={nearHero} />;

export default GoogleReviews;
